import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Clock, Calendar, Search, ClipboardCheck, Zap, Pencil, Trash2, MoreHorizontal } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  overdue: 'bg-red-100 text-red-800 border-red-200'
};

export default function ChecklistPage() {
  const { user, getAuthHeader } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    frequency: 'daily',
    assigned_to: '',
    assigned_by: '',
    start_date: new Date().toISOString().split('T')[0],
    priority: 'normal',
    allow_rollover: true  // Default: tasks roll over if not done
  });
  
  // Super admins list for "Assigned By" dropdown
  const [superAdmins, setSuperAdmins] = useState([]);
  
  // Edit/Delete states
  const [editingTask, setEditingTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleteOption, setDeleteOption] = useState('discontinue'); // 'discontinue' or 'permanent'
  
  // Filter states
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOverdue, setFilterOverdue] = useState(false);
  
  // Tab and completed instances states
  const [activeTab, setActiveTab] = useState('templates'); // 'templates' or 'completed'
  const [completedInstances, setCompletedInstances] = useState([]);
  const [completedFilterEmployee, setCompletedFilterEmployee] = useState('all');
  const [completedFilterDateFrom, setCompletedFilterDateFrom] = useState('');
  const [completedFilterDateTo, setCompletedFilterDateTo] = useState('');

  // Check if user can manage tasks - based on allowed_modules, NOT role/designation
  const canManageTasks = user?.is_owner || user?.allowed_modules?.includes('checklists');

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    if (!employeeSearch) return employees;
    return employees.filter(emp => 
      emp.name?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
      emp.employee_code?.toLowerCase().includes(employeeSearch.toLowerCase())
    );
  }, [employees, employeeSearch]);

  // Get selected employee name for display
  const selectedEmployeeName = useMemo(() => {
    if (!formData.assigned_to) return '';
    const emp = employees.find(e => e.id === formData.assigned_to);
    return emp ? `${emp.name} (${emp.employee_code})` : '';
  }, [formData.assigned_to, employees]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/checklists`, { headers: getAuthHeader() });
      let allTasks = response.data;
      
      // Apply local filters
      if (filterEmployee !== 'all') {
        allTasks = allTasks.filter(t => t.assigned_to === filterEmployee);
      }
      if (filterStatus !== 'all') {
        allTasks = allTasks.filter(t => t.status === filterStatus);
      }
      if (filterOverdue) {
        const today = new Date().toISOString().split('T')[0];
        allTasks = allTasks.filter(t => {
          const dueDate = t.due_date || t.next_due;
          return dueDate && dueDate < today && t.status !== 'completed';
        });
      }
      
      setTasks(allTasks);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  const fetchEmployees = async () => {
    // Fetch all assigners (employees + super admins) for filter dropdowns
    if (canManageTasks) {
      try {
        const response = await axios.get(`${API_URL}/users/assigners`, { headers: getAuthHeader() });
        setEmployees(response.data);
      } catch (error) {
        console.error('Failed to fetch assigners:', error);
        // Fallback to hr-employees if assigners endpoint fails
        try {
          const fallback = await axios.get(`${API_URL}/hr-employees`, { headers: getAuthHeader() });
          setEmployees(fallback.data);
        } catch (e) {
          console.error('Fallback also failed');
        }
      }
    }
  };

  // Fetch super admins for "Assigned By" dropdown
  const fetchSuperAdmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/checklists/super-admins`, { headers: getAuthHeader() });
      console.log('[Checklist] Super admins fetched:', response.data);
      
      let admins = response.data || [];
      
      // Fallback: If no super admins returned, add current user if they have checklist access
      if (admins.length === 0 && (user?.is_owner || user?.allowed_modules?.includes('checklists'))) {
        console.log('[Checklist] No super admins found, using current user as fallback');
        admins = [{ id: user.id, name: user.name || user.email }];
      }
      
      setSuperAdmins(admins);
      
      // Pre-select current user if they can manage checklists
      if (user?.is_owner || user?.allowed_modules?.includes('checklists')) {
        const currentAdmin = admins.find(a => a.id === user.id);
        if (currentAdmin) {
          setFormData(prev => ({ ...prev, assigned_by: currentAdmin.id }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch super admins:', error);
      // Fallback on error: use current user if they have access
      if (user?.is_owner || user?.allowed_modules?.includes('checklists')) {
        console.log('[Checklist] Error fetching admins, using current user as fallback');
        setSuperAdmins([{ id: user.id, name: user.name || user.email }]);
        setFormData(prev => ({ ...prev, assigned_by: user.id }));
      }
    }
  };

  const fetchCompletedInstances = async () => {
    try {
      // Build query params for date filtering
      const params = new URLSearchParams();
      if (completedFilterDateFrom) params.append('start_date', completedFilterDateFrom);
      if (completedFilterDateTo) params.append('end_date', completedFilterDateTo);
      // Default to last 30 days if no filter specified
      if (!completedFilterDateFrom && !completedFilterDateTo) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        params.append('start_date', thirtyDaysAgo.toISOString().split('T')[0]);
        params.append('end_date', new Date().toISOString().split('T')[0]);
      }
      
      const response = await axios.get(`${API_URL}/checklist-instances?${params.toString()}`, { headers: getAuthHeader() });
      
      // Filter to only completed instances
      let completed = response.data.filter(inst => inst.status === 'completed');
      
      // Apply employee filter
      if (completedFilterEmployee !== 'all') {
        completed = completed.filter(inst => inst.assigned_to === completedFilterEmployee);
      }
      
      // Sort by completion date (most recent first)
      completed.sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
      
      setCompletedInstances(completed);
    } catch (error) {
      console.error('Failed to fetch completed instances:', error);
      toast.error('Failed to fetch completed tasks');
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchEmployees();
    fetchSuperAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEmployee, filterStatus, filterOverdue]);

  useEffect(() => {
    if (activeTab === 'completed') {
      fetchCompletedInstances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, completedFilterEmployee, completedFilterDateFrom, completedFilterDateTo]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate doer is selected
    if (!formData.assigned_to) {
      toast.error('Please select a doer for this task');
      return;
    }
    
    // Validate assigned_by is selected
    if (!formData.assigned_by) {
      toast.error('Please select who is assigning this task');
      return;
    }
    
    try {
      // Get employee name for the task
      const selectedEmployee = employees.find(e => e.id === formData.assigned_to);
      // Get super admin name for assigned_by
      const selectedAdmin = superAdmins.find(a => a.id === formData.assigned_by);
      
      await axios.post(`${API_URL}/checklists`, {
        title: formData.title.toUpperCase(),
        description: (formData.description || '').toUpperCase(),
        frequency: formData.frequency,
        assigned_to: formData.assigned_to,
        assigned_to_name: selectedEmployee?.name || '',
        assigned_by_id: formData.assigned_by,
        assigned_by_name: selectedAdmin?.name || '',
        start_date: formData.start_date,  // Send as YYYY-MM-DD to avoid timezone shifts
        priority: formData.priority,
        allow_rollover: formData.allow_rollover
      }, { headers: getAuthHeader() });
      toast.success('Checklist task created successfully');
      setOpen(false);
      // Reset form but preserve assigned_by if user can manage checklists
      const defaultAssignedBy = (user?.is_owner || user?.allowed_modules?.includes('checklists')) ? formData.assigned_by : '';
      setFormData({ title: '', description: '', frequency: 'daily', assigned_to: '', assigned_by: defaultAssignedBy, start_date: new Date().toISOString().split('T')[0], priority: 'normal', allow_rollover: true });
      setEmployeeSearch('');
      setShowEmployeeDropdown(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  // Check if current user is the creator of a task
  // Users with checklist access can edit/delete all tasks, others can only edit/delete their own
  const canEditDeleteTask = (task) => {
    if (user?.is_owner || user?.allowed_modules?.includes('checklists')) return true;
    return task.created_by === user?.id;
  };
  
  // Filter tasks by selected employee
  // Since filtering is done in fetchTasks, just use tasks directly
  const filteredTasks = tasks;

  // Open edit dialog
  const openEditDialog = (task) => {
    setEditingTask({
      ...task,
      title: task.title,
      description: task.description || '',
      frequency: task.frequency,
      priority: task.priority || 'normal',
      allow_rollover: task.allow_rollover !== false  // Default to true if undefined
    });
    setEditOpen(true);
  };

  // Handle edit submit
  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    
    try {
      // Use the /edit endpoint for editing task details
      await axios.patch(`${API_URL}/checklists/${editingTask.id}/edit`, {
        title: editingTask.title.toUpperCase(),
        description: (editingTask.description || '').toUpperCase(),
        frequency: editingTask.frequency,
        priority: editingTask.priority,
        start_date: editingTask.start_date,
        assigned_to: editingTask.assigned_to,
        assigned_to_name: editingTask.assigned_to_name,
        allow_rollover: editingTask.allow_rollover
      }, { headers: getAuthHeader() });
      
      toast.success('Task updated successfully');
      setEditOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!taskToDelete) return;
    
    try {
      if (deleteOption === 'discontinue') {
        // Discontinue - keep history, stop future instances
        await axios.patch(`${API_URL}/checklists/${taskToDelete.id}/discontinue`, {}, { headers: getAuthHeader() });
        toast.success('Task discontinued. Past instances preserved, future instances stopped.');
      } else {
        // Permanent delete - remove all instances
        await axios.delete(`${API_URL}/checklists/${taskToDelete.id}?delete_instances=true`, { headers: getAuthHeader() });
        toast.success('Task and all instances deleted permanently');
      }
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      setDeleteOption('discontinue');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-muted-foreground">Manage recurring tasks with different frequencies</p>
          </div>
          {canManageTasks && !open && (
            <Button 
              onClick={() => setOpen(true)}
              data-testid="create-checklist-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Task
            </Button>
          )}
        </div>

        {/* Inline Create Task Form (replaces Dialog) */}
        {canManageTasks && open && (
          <Card className="border-2 border-primary/20 shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-primary" />
                    Create Checklist Task
                  </CardTitle>
                  <CardDescription>Add a new recurring task for Office Staff employees</CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setOpen(false);
                    setEmployeeSearch('');
                    setShowEmployeeDropdown(false);
                  }}
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        data-testid="checklist-title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="TASK TITLE"
                        style={{ textTransform: 'uppercase' }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description (Optional)</Label>
                      <Textarea
                        id="description"
                        data-testid="checklist-description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="TASK DESCRIPTION..."
                        style={{ textTransform: 'uppercase' }}
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="frequency">Frequency *</Label>
                        <select
                          id="frequency"
                          data-testid="checklist-frequency"
                          value={formData.frequency}
                          onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                          required
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="fortnightly">Fortnightly (15 days)</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="half-yearly">Half-Yearly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="start_date">Start Date *</Label>
                        <Input
                          id="start_date"
                          type="date"
                          data-testid="checklist-start-date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority *</Label>
                      <select
                        id="priority"
                        data-testid="checklist-priority"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        required
                      >
                        <option value="normal">Normal</option>
                        <option value="critical">Critical (Double penalty on miss)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assigned_by">Assigned By *</Label>
                      <select
                        id="assigned_by"
                        data-testid="checklist-assigned-by"
                        value={formData.assigned_by}
                        onChange={(e) => setFormData({ ...formData, assigned_by: e.target.value })}
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                        required
                      >
                        <option value="">Select who is assigning...</option>
                        {superAdmins.map((admin) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assigned_to">Assign To (Office Staff) *</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                        <Input
                          placeholder="Type to search employee..."
                          value={employeeSearch || selectedEmployeeName}
                          onChange={(e) => {
                            setEmployeeSearch(e.target.value);
                            setShowEmployeeDropdown(true);
                            if (e.target.value === '') {
                              setFormData({ ...formData, assigned_to: '' });
                            }
                          }}
                          onFocus={() => setShowEmployeeDropdown(true)}
                          className="pl-9"
                          data-testid="checklist-employee-search"
                        />
                        {showEmployeeDropdown && (employeeSearch || !formData.assigned_to) && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {filteredEmployees.length > 0 ? (
                              filteredEmployees.map((emp) => (
                                <div
                                  key={emp.id}
                                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                                  onClick={() => {
                                    setFormData({ ...formData, assigned_to: emp.id });
                                    setEmployeeSearch('');
                                    setShowEmployeeDropdown(false);
                                  }}
                                  data-testid={`employee-option-${emp.id}`}
                                >
                                  {emp.name} ({emp.employee_code})
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-muted-foreground">No employees found</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 pt-2">
                      <Checkbox 
                        id="allow_rollover"
                        checked={formData.allow_rollover}
                        onCheckedChange={(checked) => setFormData({...formData, allow_rollover: checked})}
                      />
                      <div className="grid gap-1.5 leading-none">
                        <Label htmlFor="allow_rollover" className="text-sm font-medium cursor-pointer">
                          Allow rollover if not completed
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Uncheck for date-specific tasks that expire at end of day (marked as ND)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setOpen(false);
                      setEmployeeSearch('');
                      setShowEmployeeDropdown(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="checklist-submit">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Task
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Tab Buttons */}
        {canManageTasks && (
          <div className="flex gap-2 border-b pb-2">
            <Button
              variant={activeTab === 'templates' ? 'default' : 'outline'}
              onClick={() => setActiveTab('templates')}
              data-testid="tab-templates"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Task Templates
            </Button>
            <Button
              variant={activeTab === 'completed' ? 'default' : 'outline'}
              onClick={() => setActiveTab('completed')}
              data-testid="tab-completed"
            >
              <Clock className="w-4 h-4 mr-2" />
              Completed Tasks
            </Button>
          </div>
        )}

        {/* Templates Table */}
        {canManageTasks && activeTab === 'templates' && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-4">
                <CardTitle className="text-lg">Checklist Tasks</CardTitle>
                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Doer:</Label>
                    <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                      <SelectTrigger className="w-44" data-testid="filter-employee-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Status:</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="checklist-overdue-filter"
                      checked={filterOverdue}
                      onChange={(e) => setFilterOverdue(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="checklist-overdue-filter" className="text-sm text-red-600 cursor-pointer">
                      Overdue Only
                    </Label>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Doer</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks
                    .sort((a, b) => {
                      // Sort by Date first (ascending), then by Priority (critical first)
                      // Completed tasks go to the bottom
                      if (a.status === 'completed' && b.status !== 'completed') return 1;
                      if (a.status !== 'completed' && b.status === 'completed') return -1;
                      
                      const dateA = new Date(a.due_date || a.next_due);
                      const dateB = new Date(b.due_date || b.next_due);
                      if (dateA.getTime() !== dateB.getTime()) {
                        return dateA - dateB;
                      }
                      // Same date - critical tasks first
                      if (a.priority === 'critical' && b.priority !== 'critical') return -1;
                      if (b.priority === 'critical' && a.priority !== 'critical') return 1;
                      return 0;
                    })
                    .map((task) => {
                      const assignedEmployee = employees.find(e => e.id === task.assigned_to);
                      const assignedName = assignedEmployee?.name || task.assigned_to_name || 'Unknown';
                      const dueDate = task.due_date || task.next_due;
                      // Only mark as overdue if due date is BEFORE today (start of day)
                      // Tasks due today should NOT be marked overdue
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isOverdue = dueDate && new Date(dueDate) < today && task.status !== 'completed';
                      const isCritical = task.priority === 'critical';
                      const canEditDelete = canEditDeleteTask(task);
                      
                      return (
                        <TableRow key={task.id} className={isOverdue ? 'bg-red-50' : isCritical ? 'bg-red-50/50' : ''}>
                          <TableCell>
                            <div className="font-medium flex items-center gap-2">
                              {task.title}
                              {isCritical && (
                                <Badge className="bg-red-600 text-white text-xs">
                                  <Zap className="w-3 h-3 mr-1" />
                                  CRITICAL
                                </Badge>
                              )}
                            </div>
                            {task.description && (
                              <div className="text-xs text-muted-foreground">{task.description.substring(0, 60)}...</div>
                            )}
                          </TableCell>
                          <TableCell>{assignedName}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{task.frequency}</Badge>
                          </TableCell>
                          <TableCell className={isOverdue ? 'text-red-600 font-medium' : ''}>
                            {dueDate ? formatDate(dueDate) : 'N/A'}
                            {isOverdue && <span className="ml-1">⚠️</span>}
                          </TableCell>
                          <TableCell>
                            <Badge variant={task.priority === 'critical' ? 'destructive' : 'secondary'}>
                              {task.priority || 'normal'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[isOverdue ? 'overdue' : (task.status === 'completed' ? 'completed' : 'pending')]}>
                              {isOverdue ? 'Overdue' : (task.status === 'completed' ? 'Done' : 'Pending')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {canEditDelete && task.status !== 'completed' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={`checklist-actions-${task.id}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditDialog(task)}>
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={() => {
                                      setTaskToDelete(task);
                                      setDeleteConfirmOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Empty state when filters return no results */}
                    {tasks.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <ClipboardCheck className="w-8 h-8 mb-2 opacity-50" />
                            <p>No tasks match the current filters.</p>
                            <Button 
                              variant="link" 
                              size="sm" 
                              onClick={() => {
                                setFilterEmployee('all');
                                setFilterStatus('all');
                                setFilterOverdue(false);
                              }}
                              className="mt-1"
                            >
                              Clear all filters
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Completed Tasks Table */}
        {canManageTasks && activeTab === 'completed' && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-4">
                <CardTitle className="text-lg">Completed Tasks</CardTitle>
                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Doer:</Label>
                    <Select value={completedFilterEmployee} onValueChange={setCompletedFilterEmployee}>
                      <SelectTrigger className="w-44" data-testid="completed-filter-employee">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">From:</Label>
                    <Input
                      type="date"
                      value={completedFilterDateFrom}
                      onChange={(e) => setCompletedFilterDateFrom(e.target.value)}
                      className="w-40"
                      data-testid="completed-filter-date-from"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">To:</Label>
                    <Input
                      type="date"
                      value={completedFilterDateTo}
                      onChange={(e) => setCompletedFilterDateTo(e.target.value)}
                      className="w-40"
                      data-testid="completed-filter-date-to"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCompletedFilterEmployee('all');
                      setCompletedFilterDateFrom('');
                      setCompletedFilterDateTo('');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Doer</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Completed On</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedInstances.map((task) => {
                    const assignedEmployee = employees.find(e => e.id === task.assigned_to);
                    const assignedName = assignedEmployee?.name || task.assigned_to_name || 'Unknown';
                    const completedAt = task.completed_at ? new Date(task.completed_at).toLocaleDateString('en-IN') : '-';
                    
                    return (
                      <TableRow key={task.id}>
                        <TableCell className="font-medium">{task.title}</TableCell>
                        <TableCell>{assignedName}</TableCell>
                        <TableCell className="capitalize">{task.frequency || '-'}</TableCell>
                        <TableCell>{formatDate(task.occurrence_date)}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            {completedAt}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {completedInstances.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <ClipboardCheck className="w-8 h-8 mb-2 opacity-50" />
                          <p>No completed tasks found for the selected filters.</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Non-Manager View - Redirect to My Tasks */}
        {!canManageTasks && (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-muted-foreground">Your tasks appear in the "My Tasks" section.</p>
              <p className="text-sm text-muted-foreground">Go to <strong>Productivity → My Tasks</strong> to view and complete your assigned tasks.</p>
            </CardContent>
          </Card>
        )}

        {/* Edit Task Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Checklist Task</DialogTitle>
              <DialogDescription>Update task details</DialogDescription>
            </DialogHeader>
            {editingTask && (
              <form onSubmit={handleEdit}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-title">Title *</Label>
                    <Input
                      id="edit-title"
                      value={editingTask.title}
                      onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                      style={{ textTransform: 'uppercase' }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-description">Description</Label>
                    <Textarea
                      id="edit-description"
                      value={editingTask.description}
                      onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-start_date">Start Date *</Label>
                      <Input
                        id="edit-start_date"
                        type="date"
                        value={editingTask.start_date ? new Date(editingTask.start_date).toISOString().split('T')[0] : ''}
                        onChange={(e) => setEditingTask({ ...editingTask, start_date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-frequency">Frequency</Label>
                      <Select
                        value={editingTask.frequency}
                        onValueChange={(value) => setEditingTask({ ...editingTask, frequency: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-assigned_to">Assign To (Office Staff) *</Label>
                    <Select
                      value={editingTask.assigned_to || ''}
                      onValueChange={(value) => {
                        const selectedEmployee = employees.find(e => e.id === value);
                        setEditingTask({ 
                          ...editingTask, 
                          assigned_to: value,
                          assigned_to_name: selectedEmployee?.name || ''
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-priority">Priority</Label>
                    <Select
                      value={editingTask.priority}
                      onValueChange={(value) => setEditingTask({ ...editingTask, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2 pt-2">
                    <Checkbox 
                      id="edit_allow_rollover"
                      checked={editingTask?.allow_rollover !== false}
                      onCheckedChange={(checked) => setEditingTask({...editingTask, allow_rollover: checked})}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="edit_allow_rollover" className="text-sm font-medium cursor-pointer">
                        Allow rollover if not completed
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Uncheck for date-specific tasks that expire at end of day (marked as ND)
                      </p>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button type="submit">Save Changes</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={(open) => {
          setDeleteConfirmOpen(open);
          if (!open) {
            setDeleteOption('discontinue');
          }
        }}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Checklist Task</AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                <span className="font-medium text-foreground">"{taskToDelete?.title}"</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="py-4 space-y-3">
              <p className="text-sm text-muted-foreground mb-3">Choose how to handle this task:</p>
              
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${deleteOption === 'discontinue' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input 
                  type="radio" 
                  name="deleteOption" 
                  value="discontinue"
                  checked={deleteOption === 'discontinue'}
                  onChange={(e) => setDeleteOption(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium">Discontinue</p>
                  <p className="text-sm text-muted-foreground">
                    Stop future occurrences but keep all past instances and scores for reporting
                  </p>
                </div>
              </label>
              
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${deleteOption === 'permanent' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input 
                  type="radio" 
                  name="deleteOption" 
                  value="permanent"
                  checked={deleteOption === 'permanent'}
                  onChange={(e) => setDeleteOption(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-red-700">Delete Permanently</p>
                  <p className="text-sm text-muted-foreground">
                    Remove task AND all past instances/scores (Cannot be undone)
                  </p>
                </div>
              </label>
            </div>
            
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete} 
                className={deleteOption === 'permanent' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
              >
                {deleteOption === 'discontinue' ? 'Discontinue' : 'Delete Permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
